import React, { useCallback } from "react";
import { IconCalendarX, IconLoader2 } from "@tabler/icons-react";
import { useAppDispatch, useAppSelector } from "@/store";
import { Button } from "@/components/ui/button";
import { flowRestarted, reservationRecovered } from "../publicBookingFlowSlice";
import type { SlotReservation } from "../publicBookingFlowTypes";
import { useReserveForZip } from "../useReserveForZip";
import { AlternateSitePrompt } from "./AlternateSitePrompt";
import { CalThrottledNotice } from "@/components/CalThrottledNotice";

const ZIP_CODE_REGEX = /^\d{5}$/;

/**
 * The booking could not be made because the time held for it is gone (AB#632): the held_time_gone
 * and slot_taken terminals. Nothing was booked.
 *
 * Offers "Find another time" and never "Try Again": resubmitting the hold that is gone can only fail
 * again. "Find another time" asks for a new hold for the ZIP the applicant already gave, through the
 * same useReserveForZip the ZIP step uses — so no slots, a closed season, an out-of-area answer and an
 * alternate site that needs accepting all end up exactly where they would from step 1. A hold, once
 * accepted, goes to reservationRecovered, which takes the flow back to the booking step; that step
 * submits on mount with the applicant's saved answers and the new hold (the new idempotency key).
 */
export const FindAnotherTimeNotice = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const savedZipCode = useAppSelector(state => state.publicBookingFlow.selectedZipCode);

  const handleReserved = useCallback((_zipCode: string, reservation: SlotReservation) => {
    dispatch(reservationRecovered(reservation));
  }, [dispatch]);

  const { reserve, loading, pendingSite, approvePendingSite, declinePendingSite, throttled, retry } = useReserveForZip(handleReserved);

  if (pendingSite) {
    return <AlternateSitePrompt site={pendingSite} onApprove={approvePendingSite} onDecline={declinePendingSite} />;
  }

  // The ZIP is saved by the step that made the first hold, so it is always here in practice. If it is
  // not, there is nothing to search with: only Start Over, which asks for it again.
  const canFindAnotherTime = ZIP_CODE_REGEX.test(savedZipCode);

  return (
    <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
      <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
        <IconCalendarX className="w-8 h-8 text-amber-600" aria-hidden="true" />
      </div>
      <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
      <p className="text-slate-600">{children}</p>
      {throttled != null && (
        <CalThrottledNotice variant="public" error={throttled} onRetry={retry} busy={loading} autoRetry className="text-left" />
      )}
      <div className="flex flex-col items-center gap-2">
        {canFindAnotherTime && (
          <Button onClick={() => reserve(savedZipCode)} disabled={loading} aria-busy={loading}>
            {loading ? (
              <span className="flex items-center gap-2">
                <IconLoader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Finding another time…
              </span>
            ) : (
              "Find another time"
            )}
          </Button>
        )}
        <Button onClick={() => dispatch(flowRestarted())} variant="outline" disabled={loading}>Start Over</Button>
      </div>
    </div>
  );
};
